import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { L10N_LOCALE, L10nLocale } from 'angular-l10n';
import { forkJoin } from 'rxjs';
import { AlertsApi, ChannelSettings } from 'src/app/hickory-api';

@Component({
  selector: 'app-alert-default-info',
  templateUrl: './alert-default-info.component.html',
  styleUrls: ['./alert-default-info.component.scss'],
  standalone: false
})
export class AlertDefaultInfoComponent implements OnInit {

  displayedColumns = ['channel', 'emergency', 'nonEmergency'];
  dataSource = new MatTableDataSource<ChannelRow>();
  isLoading = false;
  errorResponse = false;
  errorMessage = '';
  tableData: ChannelRow[] = [];

  constructor(
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    public dialogRef: MatDialogRef<AlertDefaultInfoComponent>,
    public alertsApi: AlertsApi,
  ) { }

  ngOnInit(): void {
    this.getData();
  }

  getData() {
    this.isLoading = true;
    this.errorResponse = false;
    this.errorMessage = '';

    const emergencyResponse = this.alertsApi.getSystemDefaultSettings('Default', 'Emergency');
    const nonEmergencyResponse = this.alertsApi.getSystemDefaultSettings('Default', 'NonEmergency');
    const joined = forkJoin([emergencyResponse, nonEmergencyResponse]);
    joined.subscribe({
      next: ([emergency, nonEmergency]) => {
        this.transformApiData(emergency.item?.settings?.channels, nonEmergency.item?.settings?.channels);
        this.dataSource.data = this.tableData;
      },
      error: _ => {
        this.errorResponse = true;
        this.errorMessage = 'There was an error retrieving the data. Please try again.';
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  transformApiData(emergencyChannels: ChannelSettings, nonEmergencyChannels: ChannelSettings) {
    this.tableData = [];

    this.tableData.push({
      channelName: 'Mobile App',
      channelEnabledForEmergency: emergencyChannels?.push?.send,
      channelEnabledForNonEmergency: nonEmergencyChannels?.push?.send
    });

    this.tableData.push({
      channelName: 'myAlerts App',
      channelEnabledForEmergency: emergencyChannels?.mobileBroadcast?.send,
      channelEnabledForNonEmergency: nonEmergencyChannels?.mobileBroadcast?.send
    });

    this.tableData.push({
      channelName: 'Email',
      channelEnabledForEmergency: emergencyChannels?.email?.send,
      channelEnabledForNonEmergency: nonEmergencyChannels?.email?.send
    });

    this.tableData.push({
      channelName: 'SMS: Primary',
      channelEnabledForEmergency: !emergencyChannels?.textMessage?.send ? false : emergencyChannels?.textMessage?.primary,
      channelEnabledForNonEmergency: !nonEmergencyChannels?.textMessage?.send ? false : nonEmergencyChannels?.textMessage?.primary
    });

    this.tableData.push({
      channelName: 'SMS: Secondary',
      channelEnabledForEmergency: !emergencyChannels?.textMessage?.send ? false : emergencyChannels?.textMessage?.secondary,
      channelEnabledForNonEmergency: !nonEmergencyChannels?.textMessage?.send ? false : nonEmergencyChannels?.textMessage?.secondary
    });

    this.tableData.push({
      channelName: 'Voice: Cell Phone',
      channelEnabledForEmergency: !emergencyChannels?.voice?.send ? false : emergencyChannels?.voice?.cellPhone,
      channelEnabledForNonEmergency: !nonEmergencyChannels?.voice?.send ? false : nonEmergencyChannels?.voice?.cellPhone
    });

    this.tableData.push({
      channelName: 'Voice: Business Phone',
      channelEnabledForEmergency: !emergencyChannels?.voice?.send ? false : emergencyChannels?.voice?.businessPhone,
      channelEnabledForNonEmergency: !nonEmergencyChannels?.voice?.send ? false : nonEmergencyChannels?.voice?.businessPhone
    });

    this.tableData.push({
      channelName: 'Voice: Home Phone',
      channelEnabledForEmergency: !emergencyChannels?.voice?.send ? false : emergencyChannels?.voice?.homePhone,
      channelEnabledForNonEmergency: !nonEmergencyChannels?.voice?.send ? false : nonEmergencyChannels?.voice?.homePhone
    });

    this.tableData.push({
      channelName: 'ETN',
      channelEnabledForEmergency: emergencyChannels?.etn?.send,
      channelEnabledForNonEmergency: nonEmergencyChannels?.etn?.send
    });

    this.tableData.push({
      channelName: 'Fax',
      channelEnabledForEmergency: emergencyChannels?.fax?.send,
      channelEnabledForNonEmergency: nonEmergencyChannels?.fax?.send
    });
  }

}

export interface ChannelRow {
  channelName: string,
  channelEnabledForEmergency: boolean,
  channelEnabledForNonEmergency: boolean,
}
